import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useCustomers } from '@/data/customers';
import { useBillableLoads, useCreateInvoiceFromLoads, useInvoices } from '@/data/invoices';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';
import { TableScroll } from '@/components/ui/table-scroll';
import { formatDate, formatMoney } from '@/lib/format';
import type { InvoiceDisplayStatus } from '@breaker/shared-types';
import type { StatusTone } from '@/lib/status';

/**
 * Invoices list + the "bill completed work" flow.
 *
 * The status shown is the DERIVED one (`settlement.displayStatus`), not the
 * stored column: part-paid and overdue are facts about payments and the due
 * date, so a stored flag would be wrong from the first midnight nobody ran a
 * sweep. See deriveSettlement in @breaker/db.
 */

const STATUS_TONE: Record<InvoiceDisplayStatus, StatusTone> = {
  draft: 'neutral',
  sent: 'info',
  part_paid: 'active',
  paid: 'success',
  overdue: 'danger',
  credited: 'neutral',
  void: 'neutral',
};

const STATUS_LABEL: Record<InvoiceDisplayStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  credited: 'Credited',
  void: 'Void',
};

export function InvoicesPage() {
  const { fleet } = useActiveFleet();
  const fleetId = fleet?.id ?? null;
  const navigate = useNavigate();
  const [billing, setBilling] = useState(false);

  const invoicesQuery = useInvoices(fleetId);
  const billableQuery = useBillableLoads(fleetId);

  const unbilledCount = billableQuery.data?.length ?? 0;

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            Invoices
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            Bill completed work, record what comes in, and correct mistakes with
            credit notes.
          </p>
        </div>
        <Button size="sm" onClick={() => setBilling(true)} disabled={unbilledCount === 0}>
          {unbilledCount > 0 ? `Bill ${unbilledCount} completed job${unbilledCount === 1 ? '' : 's'}` : 'Nothing to bill'}
        </Button>
      </header>

      {invoicesQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : invoicesQuery.isError ? (
        <EmptyState
          title="Could not load invoices"
          description={(invoicesQuery.error as Error)?.message ?? 'Server error.'}
        />
      ) : (invoicesQuery.data ?? []).length === 0 ? (
        <EmptyState
          title="No invoices yet"
          description={
            unbilledCount > 0
              ? `You have ${unbilledCount} completed job${unbilledCount === 1 ? '' : 's'} ready to bill.`
              : 'Completed jobs appear here once they are ready to bill.'
          }
        />
      ) : (
        <>
        {/*
          Narrow viewports get CARDS, not a horizontally scrolling table.
          Outstanding is the number a fleet owner checks from the cab and
          status is the second — putting both behind a sideways scroll on the
          phone is the wrong trade for a product whose entry point is
          owner-drivers. The table below is unchanged for md and up, where the
          columns genuinely fit.
        */}
        <ul className="md:hidden space-y-2">
          {(invoicesQuery.data ?? []).map((inv) => {
            const status = inv.settlement?.displayStatus ?? 'draft';
            const outstanding = inv.settlement?.outstandingPence ?? inv.grossPence;
            return (
              <li key={inv.id} className="surface rounded-lg">
                <Link
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: inv.id }}
                  className="block px-4 py-3 active:bg-breaker-ground-2"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium text-breaker-ink">
                      {inv.numberText ?? 'Draft'}
                    </span>
                    <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
                  </div>
                  <div className="mt-1 flex items-end justify-between gap-3">
                    <span className="text-sm text-breaker-ink-2 min-w-0 truncate">
                      {inv.customerSnapshot?.name ?? '—'}
                    </span>
                    <span className="text-right shrink-0">
                      <span className="block text-base font-semibold tabular-nums text-breaker-ink">
                        {formatMoney(outstanding)}
                      </span>
                      <span className="block text-2xs uppercase tracking-wider text-breaker-mute">
                        {outstanding > 0 ? 'outstanding' : 'settled'}
                      </span>
                    </span>
                  </div>
                  {inv.dueAt && outstanding > 0 ? (
                    <div className="mt-1 text-xs text-breaker-mute">
                      Due {formatDate(inv.dueAt)}
                    </div>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="surface rounded-lg overflow-hidden hidden md:block">
          <TableScroll>
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wider text-breaker-mute border-b border-breaker-line-2">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Number</th>
                  <th className="text-left font-medium px-4 py-2.5">Customer</th>
                  <th className="text-left font-medium px-4 py-2.5">Issued</th>
                  <th className="text-left font-medium px-4 py-2.5">Due</th>
                  <th className="text-right font-medium px-4 py-2.5">Total</th>
                  <th className="text-right font-medium px-4 py-2.5">Outstanding</th>
                  <th className="text-left font-medium px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {(invoicesQuery.data ?? []).map((inv) => {
                  const status = inv.settlement?.displayStatus ?? 'draft';
                  return (
                    <tr
                      key={inv.id}
                      className="border-b border-breaker-line-2 last:border-0 hover:bg-breaker-ground-2 cursor-pointer"
                      onClick={() =>
                        navigate({ to: '/invoices/$invoiceId', params: { invoiceId: inv.id } })
                      }
                    >
                      <td className="px-4 py-3 font-medium text-breaker-ink">
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: inv.id }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Never compose this client-side: the prefix is
                              per-fleet and frozen on the row at issue. */}
                          {inv.numberText ?? 'Draft'}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-breaker-ink-2">
                        {inv.customerSnapshot?.name ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-breaker-ink-2">
                        {inv.issuedAt ? formatDate(inv.issuedAt) : '—'}
                      </td>
                      <td className="px-4 py-3 text-breaker-ink-2">
                        {inv.dueAt ? formatDate(inv.dueAt) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(inv.grossPence)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatMoney(inv.settlement?.outstandingPence ?? inv.grossPence)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </div>
        </>
      )}

      {billing ? (
        <BillWorkDialog fleetId={fleetId} onClose={() => setBilling(false)} />
      ) : null}
    </div>
  );
}

/**
 * Pick a customer, then pick which of their completed jobs to bill.
 *
 * Customer first, not jobs first: an invoice has exactly one customer, and
 * letting someone tick jobs across two customers only to be told at the end
 * that it cannot be one invoice is a worse conversation than narrowing first.
 */
function BillWorkDialog({
  fleetId,
  onClose,
}: {
  fleetId: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const customersQuery = useCustomers(fleetId, { limit: 100 });
  const [customerId, setCustomerId] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const billableQuery = useBillableLoads(fleetId, customerId || undefined);
  const create = useCreateInvoiceFromLoads(fleetId);

  const loads = useMemo(
    () => (customerId ? (billableQuery.data ?? []) : []),
    [customerId, billableQuery.data],
  );

  const total = loads
    .filter((l) => selected.has(l.loadId))
    .reduce((sum, l) => sum + l.netPence, 0);

  function toggle(loadId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(loadId)) next.delete(loadId);
      else next.add(loadId);
      return next;
    });
  }

  async function submit() {
    if (!customerId || selected.size === 0) return;
    const result = await create.mutateAsync({
      customerId,
      loadIds: [...selected],
    });
    onClose();
    navigate({ to: '/invoices/$invoiceId', params: { invoiceId: result.invoice.id } });
  }

  return (
    <Dialog open onClose={onClose} title="Bill completed work">
      <div className="space-y-4">
        <Field label="Customer" htmlFor="billCustomer">
          <select
            id="billCustomer"
            className="w-full rounded border border-breaker-line-2 bg-white px-3 py-2 text-sm"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setSelected(new Set());
            }}
          >
            <option value="">Choose a customer…</option>
            {(customersQuery.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        {customerId ? (
          billableQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : loads.length === 0 ? (
            <p className="text-sm text-breaker-mute py-4">
              No completed jobs waiting to be billed for this customer.
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto border border-breaker-line-2 rounded">
              <table className="w-full text-sm">
                <tbody>
                  {loads.map((l) => (
                    <tr key={l.loadId} className="border-b border-breaker-line-2 last:border-0">
                      <td className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={selected.has(l.loadId)}
                          onChange={() => toggle(l.loadId)}
                          aria-label={`Bill ${l.reference}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">{l.reference}</td>
                      <td className="px-3 py-2 text-breaker-ink-2">
                        {l.completedAt ? formatDate(l.completedAt) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(l.netPence)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {create.isError ? (
          <p className="text-sm text-breaker-red">{(create.error as Error).message}</p>
        ) : null}

        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-breaker-mute">
            {selected.size > 0
              ? `${selected.size} job${selected.size === 1 ? '' : 's'} · ${formatMoney(total)} net`
              : 'Select the jobs to bill.'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={selected.size === 0 || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create draft'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
